import React, { useEffect, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, MessageCircle, HelpCircle, Send, Loader2, 
  CheckCircle2, Clock, Lock, Globe, Trash2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { PageHeader } from '../components/common/PageHeader';

export default function TripFAQ() {
  const { tripId } = useParams();
  const context = useOutletContext();
  const { isOrganizer } = context || {};
  const { api, user } = useAuth();
  
  const [faqItems, setFaqItems] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [answerDialogOpen, setAnswerDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', category: '' });
  const [questionForm, setQuestionForm] = useState({ content: '', question_type: 'public' });
  const [answerForm, setAnswerForm] = useState({ answer: '', make_public: false });

  useEffect(() => {
    fetchData();
  }, [tripId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [faqRes, questionsRes] = await Promise.all([
        api.get(`/trips/${tripId}/faq/items`),
        api.get(`/trips/${tripId}/faq/questions`)
      ]);
      setFaqItems(faqRes.data);
      setQuestions(questionsRes.data);
    } catch (error) {
      console.error('Error fetching FAQ data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFAQ = async (e) => {
    e.preventDefault();
    if (!faqForm.question || !faqForm.answer) {
      toast.error('Preencha pergunta e resposta');
      return;
    }
    try {
      await api.post(`/trips/${tripId}/faq/items`, {
        ...faqForm,
        trip_id: tripId
      });
      toast.success('FAQ adicionado!');
      setFaqDialogOpen(false);
      setFaqForm({ question: '', answer: '', category: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar FAQ');
    }
  };

  const handleDeleteFAQ = async (itemId) => {
    if (!window.confirm('Excluir este item do FAQ?')) return;
    try {
      await api.delete(`/trips/${tripId}/faq/items/${itemId}`);
      toast.success('FAQ excluído');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleCreateQuestion = async (e) => {
    e.preventDefault();
    if (!questionForm.content) {
      toast.error('Digite sua pergunta');
      return;
    }
    try {
      await api.post(`/trips/${tripId}/faq/questions`, {
        ...questionForm,
        trip_id: tripId
      });
      toast.success('Pergunta enviada!');
      setQuestionDialogOpen(false);
      setQuestionForm({ content: '', question_type: 'public' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao enviar pergunta');
    }
  };

  const handleAnswerQuestion = async (e) => {
    e.preventDefault();
    if (!answerForm.answer || !selectedQuestion) return;
    try {
      await api.post(`/trips/${tripId}/faq/questions/${selectedQuestion.id}/answer`, answerForm);
      toast.success('Resposta enviada!');
      setAnswerDialogOpen(false);
      setSelectedQuestion(null);
      setAnswerForm({ answer: '', make_public: false });
      fetchData();
    } catch (error) {
      toast.error('Erro ao responder');
    }
  };

  const openAnswerDialog = (question) => {
    setSelectedQuestion(question);
    setAnswerForm({ answer: '', make_public: question.question_type === 'public' });
    setAnswerDialogOpen(true);
  };

  const pendingQuestions = questions.filter(q => q.status === 'pending');
  const answeredQuestions = questions.filter(q => q.status === 'answered');

  return (
    <div className="space-y-6">
      {/* Question Dialog - movido para fora do header */}
      <Dialog open={questionDialogOpen} onOpenChange={setQuestionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar uma dúvida</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateQuestion} className="space-y-4 mt-4">
            <div>
              <Label>Sua pergunta</Label>
              <Textarea
                value={questionForm.content}
                onChange={(e) => setQuestionForm({ ...questionForm, content: e.target.value })}
                placeholder="Digite sua dúvida..."
                className="mt-1"
                rows={3}
                data-testid="question-content"
              />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select
                value={questionForm.question_type}
                onValueChange={(v) => setQuestionForm({ ...questionForm, question_type: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Pública (todos podem ver)
                    </div>
                  </SelectItem>
                  <SelectItem value="private">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Privada (só o organizador)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setQuestionDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600">
                Enviar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* FAQ Dialog - movido para fora do header */}
      <Dialog open={faqDialogOpen} onOpenChange={setFaqDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar ao FAQ</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateFAQ} className="space-y-4 mt-4">
            <div>
              <Label>Pergunta</Label>
              <Input
                value={faqForm.question}
                onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                placeholder="Ex: Qual o horário de check-in?"
                className="mt-1"
                data-testid="faq-question"
              />
            </div>
            <div>
              <Label>Resposta</Label>
              <Textarea
                value={faqForm.answer}
                onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
                placeholder="Resposta detalhada..."
                className="mt-1"
                rows={3}
                data-testid="faq-answer"
              />
            </div>
            <div>
              <Label>Categoria (opcional)</Label>
              <Input
                value={faqForm.category}
                onChange={(e) => setFaqForm({ ...faqForm, category: e.target.value })}
                placeholder="Ex: Hospedagem, Transporte..."
                className="mt-1"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setFaqDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 rounded-full bg-orange-500 hover:bg-orange-600">
                Adicionar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Header Padronizado */}
      <PageHeader
        title="Central de Dúvidas"
        subtitle="Perguntas frequentes e chat com o organizador"
        helpScreen="faq"
        actions={
          <>
            <Button variant="outline" className="rounded-full" onClick={() => setQuestionDialogOpen(true)} data-testid="ask-question-btn">
              <Send className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Enviar dúvida</span>
              <span className="sm:hidden">Dúvida</span>
            </Button>
            {isOrganizer && (
              <Button className="rounded-full bg-orange-500 hover:bg-orange-600" onClick={() => setFaqDialogOpen(true)} data-testid="add-faq-btn">
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Adicionar FAQ</span>
                <span className="sm:hidden">FAQ</span>
              </Button>
            )}
          </>
        }
      />

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : (
        <>
          {/* FAQ Section */}
          <Card className="border-slate-100 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-orange-500" />
                Perguntas Frequentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {faqItems.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nenhuma pergunta frequente cadastrada</p>
              ) : (
                <Accordion type="single" collapsible className="w-full">
                  {faqItems.map((item) => (
                    <AccordionItem key={item.id} value={item.id}>
                      <AccordionTrigger className="text-left hover:no-underline">
                        <div className="flex items-center gap-2">
                          {item.category && (
                            <Badge variant="outline" className="text-xs">{item.category}</Badge>
                          )}
                          <span>{item.question}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="flex justify-between items-start">
                          <p className="text-slate-600 dark:text-slate-400">{item.answer}</p>
                          {isOrganizer && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 h-8 w-8"
                              onClick={() => handleDeleteFAQ(item.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </CardContent>
          </Card>

          {/* Questions from Members */}
          {isOrganizer && pendingQuestions.length > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-700">
                  <Clock className="w-5 h-5" />
                  Dúvidas Pendentes ({pendingQuestions.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {pendingQuestions.map((q) => (
                  <div key={q.id} className="bg-white rounded-xl p-4 border border-orange-100">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-slate-900 dark:text-white">{q.asked_by_name}</span>
                          <Badge className={q.question_type === 'private' ? 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300' : 'bg-sky-100 text-sky-700'}>
                            {q.question_type === 'private' ? <Lock className="w-3 h-3 mr-1" /> : <Globe className="w-3 h-3 mr-1" />}
                            {q.question_type === 'private' ? 'Privada' : 'Pública'}
                          </Badge>
                        </div>
                        <p className="text-slate-600 dark:text-slate-400">{q.content}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {format(parseISO(q.created_at), "dd/MM 'às' HH:mm", { locale: ptBR })}
                        </p>
                      </div>
                      <Button size="sm" className="rounded-full" onClick={() => openAnswerDialog(q)}>
                        Responder
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Answered Questions */}
          <Card className="border-slate-100 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-500" />
                Dúvidas Respondidas
              </CardTitle>
            </CardHeader>
            <CardContent>
              {answeredQuestions.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nenhuma dúvida respondida ainda</p>
              ) : (
                <div className="space-y-4">
                  {answeredQuestions.map((q) => (
                    <div key={q.id} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-slate-900 dark:text-white">{q.asked_by_name}</span>
                        {q.question_type === 'private' && !q.is_public_answer && (
                          <Badge variant="outline" className="text-xs">
                            <Lock className="w-3 h-3 mr-1" />
                            Privada
                          </Badge>
                        )}
                      </div>
                      <p className="text-slate-700 dark:text-slate-300 mb-2">{q.content}</p>
                      <div className="bg-green-50 rounded-lg p-3 ml-4">
                        <div className="flex items-center gap-1 text-green-700 text-sm mb-1">
                          <CheckCircle2 className="w-4 h-4" />
                          Resposta do organizador
                        </div>
                        <p className="text-slate-600 dark:text-slate-400">{q.answer}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Answer Dialog */}
      <Dialog open={answerDialogOpen} onOpenChange={setAnswerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Responder dúvida</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="bg-slate-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-slate-500 dark:text-slate-400">Pergunta de {selectedQuestion?.asked_by_name}:</p>
              <p className="text-slate-700 dark:text-slate-300">{selectedQuestion?.content}</p>
            </div>
            <form onSubmit={handleAnswerQuestion} className="space-y-4">
              <div>
                <Label>Sua resposta</Label>
                <Textarea
                  value={answerForm.answer}
                  onChange={(e) => setAnswerForm({ ...answerForm, answer: e.target.value })}
                  placeholder="Digite sua resposta..."
                  className="mt-1"
                  rows={3}
                />
              </div>
              {selectedQuestion?.question_type === 'private' && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="make_public"
                    checked={answerForm.make_public}
                    onChange={(e) => setAnswerForm({ ...answerForm, make_public: e.target.checked })}
                  />
                  <Label htmlFor="make_public" className="font-normal">
                    Publicar resposta para todos verem
                  </Label>
                </div>
              )}
              <div className="flex gap-3">
                <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setAnswerDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 rounded-full bg-green-500 hover:bg-green-600">
                  Enviar resposta
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
